/* errorLocation — split a BeXO validator error into its leading LOCATION
 * prefix (for distinct styling) and derive best-effort SEARCH TOKENS that
 * point at the offending spot in the JSON so the row can jump to it.
 *
 * Errors arrive verbatim as a flat string, each prefixed with a path, e.g.
 *   steps[2].with.email: must be a valid email
 *   capabilities[3].operation_type: unknown value 'fetch'
 *   surfaces[0] exit[1]: dangling reference
 *   components[3] (button_x): Button.label is required
 *   steps[0]: unknown capability 'cap_nope'
 *
 * The prefix is everything before the first ': '. We derive an ORDERED list
 * of candidate tokens, most-specific first:
 *   1. the offending VALUE named in the message — the value the user actually
 *      typed (e.g. `cap_nope`, `fetch`, `ghost`, `foo($.y)`); this is the most
 *      precise pointer because it lands on the EXACT line, not the parent.
 *   2. a concrete id in the prefix — (id) or 'id' or "id" — which appears
 *      literally in the doc.
 *   3. the last dotted key in the prefix (or the parent key of a trailing
 *      array index).
 * A caller tries each in order and reveals the first that resolves. */

export interface ParsedError {
    /** Verbatim leading location prefix (text before the first ': '). */
    prefix: string;
    /** The remaining human message (after the first ': '). May be empty. */
    message: string;
    /** Ordered best-effort search tokens (most specific first), deduped and
     * with falsy entries dropped. Empty when nothing usable resolves. */
    tokens: string[];
    /** Back-compat single token = tokens[0], or null when there are none. */
    token: string | null;
}

/** The offending VALUE quoted in the message. Prefer the user's actual value
 * in `got '<X>'` / `got "<X>"`; otherwise the FIRST single/double-quoted token
 * in the message. We deliberately never pick from a `one of [ ... ]` list of
 * VALID options — those messages always also carry a `got '<X>'`, so preferring
 * the got-value keeps us off the options list. */
function valueFromMessage(message: string): string | null {
    if (!message) return null;
    const got = /\bgot\s+['"]([^'"]+)['"]/i.exec(message);
    if (got && got[1].trim()) return got[1].trim();
    const quoted = /['"]([^'"]+)['"]/.exec(message);
    if (quoted && quoted[1].trim()) return quoted[1].trim();
    return null;
}

/** Concrete id inside the prefix: (id) or 'id' or "id". These strings appear
 * literally in the document. */
function idFromPrefix(prefix: string): string | null {
    const paren = /\(([^)]+)\)/.exec(prefix);
    if (paren && paren[1].trim()) return paren[1].trim();
    const quoted = /['"]([^'"]+)['"]/.exec(prefix);
    if (quoted && quoted[1].trim()) return quoted[1].trim();
    return null;
}

/** Last dotted key in the prefix (e.g. 'email', 'operation_type'), ignoring
 * array-index suffixes. Falls back to the parent key of a trailing index. */
function keyFromPrefix(prefix: string): string | null {
    // Tokenize into key / index segments across both '.' and ' ' separators.
    // e.g. "steps[2].with.email" → steps, with, email
    //      "surfaces[0] exit[1]" → surfaces, exit
    const segments = prefix
        .split(/[.\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    if (segments.length === 0) return null;

    // Walk from the end for the last segment that carries a bare key
    // (strip a trailing [n]); that key is the most specific field name.
    for (let i = segments.length - 1; i >= 0; i--) {
        const key = segments[i].replace(/\[\d+\]\s*$/, '').trim();
        if (key) return key;
    }
    return null;
}

export function parseValidatorError(raw: string): ParsedError {
    const text = String(raw);
    const sep = text.indexOf(': ');
    const prefix = sep >= 0 ? text.slice(0, sep) : text;
    const message = sep >= 0 ? text.slice(sep + 2) : '';

    // Most specific first: the offending value named in the message (lands on
    // the EXACT line), then a concrete id in the prefix, then the last key.
    const candidates = [
        valueFromMessage(message),
        idFromPrefix(prefix),
        keyFromPrefix(prefix)
    ];
    const tokens: string[] = [];
    for (const c of candidates) {
        const t = c && c.trim();
        if (t && !tokens.includes(t)) tokens.push(t);
    }

    return {
        prefix,
        message,
        tokens,
        token: tokens.length ? tokens[0] : null
    };
}
